import { useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import DefaultInput from "@/components/forms/inputs/defaultInput";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { RootState } from "@/components/state/store";
import { SubjectSearchSelect } from "@/components/subjectSearch/subjectSearchSelect";
import {
  createPromiseRequest,
  getLmsManagerRequestConfig,
  getLmsManagerRequestParameters,
  LmsMode,
} from "@/components/lms/lms.utils";
import LmsCustomIntervalFields, {
  buildCustomIntervalPayload,
  getInvalidCustomIntervalTargets,
  getDefaultCustomIntervalState,
  LmsCustomIntervalValidationTarget,
  LmsCustomIntervalState,
} from "@/components/lms/lmsCustomIntervalFields";
import styles from "@/components/lms/lmsEnrolmentCreateEditModals.module.css";
import { useEnrolmentAssignmentTargetSelection } from "@/components/lms/enrolmentAssignmentTargetSelection";
import { localizeLmsContractEligibilityError } from "@/components/lms/lmsQuickSearchEligibility";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import useFormSubmission from "@/helper/customHooks/useFormSubmission";

interface Props {
  open: boolean;
  onClose: () => void;
  onCompleted: () => void;
  assignment?: any | null;
  mode?: LmsMode;
}

type AssignmentEditValidationTarget =
  | "participantCompany"
  | "participants"
  | "companies"
  | "departmentCompany"
  | "departments"
  | "fallbackTarget"
  | LmsCustomIntervalValidationTarget;

const buildInitialIntervalState = (assignment: any): LmsCustomIntervalState => {
  const hasCustomCompletion =
    assignment?.inherited_from?.completion_interval_in_days === "assignment";
  const hasCustomValidity =
    assignment?.inherited_from?.validity_interval_in_days === "assignment";

  return {
    useCustomIntervals: hasCustomCompletion || hasCustomValidity,
    completionIntervalInDays:
      hasCustomCompletion && assignment?.completion_interval_in_days != null
        ? String(assignment.completion_interval_in_days)
        : "",
    validityIntervalInDays:
      hasCustomValidity && assignment?.validity_interval_in_days != null
        ? String(assignment.validity_interval_in_days)
        : "",
  };
};

export default function LmsEnrolmentAssignmentEditModal({
  open,
  onClose,
  onCompleted,
  assignment,
  mode = "administrator",
}: Props) {
  const { t } = useTranslation();
  const subdomain = window.location.hostname.split(".")[0];
  const selectedChildCompany = useSelector((state: RootState) => state.parent);
  const [includeChildCompanies, setIncludeChildCompanies] = useState(false);
  const [customIntervals, setCustomIntervals] =
    useState<LmsCustomIntervalState>(getDefaultCustomIntervalState);
  const [validationErrors, setValidationErrors] = useState<
    AssignmentEditValidationTarget[]
  >([]);
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<
      Record<string, never>,
      AssignmentEditValidationTarget
    >({ fieldOrder: [] });
  const { handleError, clearError, errorModal } = useErrorModal(
    t("edit_enrolment_assignment")
  );

  const selectedAssignmentType = String(assignment?.type ?? "");
  const courseId = String(assignment?.course?.id ?? assignment?.course ?? "");
  const assignmentId = String(assignment?.id ?? "");
  const isReadOnly = Boolean(assignment?.is_read_only);
  const {
    selectedTargetOptions,
    selectedDepartments,
    selectedDepartmentCompanies,
    selectedParticipantCompanies,
    usesManagerCompanyScope,
    managerLockedCompanyDisplayName,
    setSelectedDepartments,
    setSelectedDepartmentCompanies,
    setSelectedParticipantCompanies,
    setSelectedParticipants,
    handleGenericTargetChange,
    loadParticipantOptions,
    loadCompanyOptions,
    loadDepartmentOptions,
    loadAllDepartmentOptions,
    departmentOptionsScopeKey,
    loadFallbackSubjectOptions,
    getSelectionBlockedMessage,
    buildAssignmentTargetPayload,
    initializeSelectionsFromAssignment,
  } = useEnrolmentAssignmentTargetSelection({
    selectedAssignmentType,
    mode,
    subdomain,
    selectedCompanySubjectId: selectedChildCompany.subjectId,
    selectedCompanyName: selectedChildCompany.companyName,
  });
  const managerRequestConfig = useMemo(
    () => getLmsManagerRequestConfig(mode, selectedChildCompany.subjectId),
    [mode, selectedChildCompany.subjectId]
  );
  const managerRequestParameters = useMemo(
    () => getLmsManagerRequestParameters(mode, selectedChildCompany.subjectId),
    [mode, selectedChildCompany.subjectId]
  );

  useEffect(() => {
    if (!open || !assignment) {
      return;
    }

    setValidationErrors([]);
    clearError();
    setIncludeChildCompanies(Boolean(assignment.include_child_companies));
    setCustomIntervals(buildInitialIntervalState(assignment));
    initializeSelectionsFromAssignment(assignment);
  }, [open, assignment, initializeSelectionsFromAssignment, clearError]);

  const clearValidationErrors = (
    ...targets: AssignmentEditValidationTarget[]
  ) => {
    setValidationErrors((current) =>
      current.filter((target) => !targets.includes(target))
    );
  };

  const getInvalidTargets = (): AssignmentEditValidationTarget[] => {
    const invalidTargets: AssignmentEditValidationTarget[] = [];
    if (selectedAssignmentType === "multiple_participants") {
      if (
        !usesManagerCompanyScope &&
        selectedParticipantCompanies.length === 0
      ) {
        invalidTargets.push("participantCompany");
      }
      if (selectedTargetOptions.length === 0) {
        invalidTargets.push("participants");
      }
    } else if (selectedAssignmentType === "multiple_companies") {
      if (!usesManagerCompanyScope && selectedTargetOptions.length === 0) {
        invalidTargets.push("companies");
      }
    } else if (selectedAssignmentType === "multiple_departments") {
      if (
        !usesManagerCompanyScope &&
        selectedDepartmentCompanies.length === 0
      ) {
        invalidTargets.push("departmentCompany");
      }
      if (selectedTargetOptions.length === 0) {
        invalidTargets.push("departments");
      }
    } else if (selectedTargetOptions.length === 0) {
      invalidTargets.push("fallbackTarget");
    }

    invalidTargets.push(...getInvalidCustomIntervalTargets(customIntervals));
    return invalidTargets;
  };

  const contextUnavailable =
    !assignmentId ||
    !courseId ||
    (selectedAssignmentType === "multiple_companies" &&
      usesManagerCompanyScope &&
      selectedTargetOptions.length === 0);

  const handleSave = async () => {
    if (isReadOnly) {
      return;
    }
    const invalidTargets = getInvalidTargets();
    setValidationErrors(invalidTargets);
    if (invalidTargets.length > 0) {
      focusFirstInvalidField({}, invalidTargets);
      return;
    }

    const assignmentTargetPayload = buildAssignmentTargetPayload();
    const intervalPayload = buildCustomIntervalPayload(customIntervals, {
      includeNullForEmpty: true,
    });

    try {
      await createPromiseRequest({
        ...managerRequestConfig,
        entity: "enrolmentAssignment",
        method: "put",
        entityId: assignmentId,
        parametersToRender: managerRequestParameters,
        config: {
          headers: {
            "Content-Type": "application/json",
          },
        },
        submitData: {
          course: courseId,
          type: selectedAssignmentType,
          include_child_companies:
            selectedAssignmentType === "multiple_companies"
              ? includeChildCompanies
              : false,
          ...intervalPayload,
          ...assignmentTargetPayload,
        },
      });
      onCompleted();
    } catch (requestError) {
      handleError(localizeLmsContractEligibilityError(requestError, t));
    }
  };
  const { handleSubmit, isSubmitting } = useFormSubmission({
    onSubmit: handleSave,
    disabled: isReadOnly || contextUnavailable,
  });

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          if (!isSubmitting) {
            onClose();
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("edit_enrolment_assignment")}</DialogTitle>
        <form
          style={{ display: "contents" }}
          onSubmit={handleSubmit}
          noValidate
        >
          <DialogContent className={styles.dialogContent}>
            {isReadOnly ? (
              <Typography>{t("enrolment_assignment_read_only")}</Typography>
            ) : (
              <>
                {selectedAssignmentType === "multiple_participants" && (
                  <>
                    {!usesManagerCompanyScope && (
                      <div ref={registerValidationTarget("participantCompany")}>
                        <FormFieldLabel
                          label={t("company")}
                          htmlFor="lms-assignment-participant-company"
                          mandatory
                        />
                        <SubjectSearchSelect
                          name="lms-assignment-participant-company"
                          isMulti={false}
                          value={selectedParticipantCompanies[0]}
                          loadOptions={loadCompanyOptions}
                          onChange={(value: any) => {
                            clearValidationErrors(
                              "participantCompany",
                              "participants"
                            );
                            if (!value) {
                              setSelectedParticipantCompanies([]);
                              setSelectedParticipants([]);
                              return;
                            }
                            setSelectedParticipantCompanies([value]);
                            setSelectedParticipants([]);
                          }}
                          isClearable={true}
                          placeholder={t("search_placeholder")}
                          getSelectionBlockedMessage={
                            getSelectionBlockedMessage
                          }
                        />
                        {validationErrors.includes("participantCompany") && (
                          <Typography
                            color="error"
                            variant="caption"
                            role="alert"
                          >
                            {t("field_required")}
                          </Typography>
                        )}
                      </div>
                    )}
                    <div ref={registerValidationTarget("participants")}>
                      <FormFieldLabel
                        label={t("persons")}
                        htmlFor="lms-assignment-participants"
                        mandatory
                      />
                      <SubjectSearchSelect
                        name="lms-assignment-participants"
                        isMulti={true}
                        value={selectedTargetOptions}
                        loadOptions={loadParticipantOptions}
                        onChange={(value: any) => {
                          clearValidationErrors("participants");
                          handleGenericTargetChange(value);
                        }}
                        isClearable={true}
                        isDisabled={
                          !usesManagerCompanyScope &&
                          selectedParticipantCompanies.length === 0
                        }
                        placeholder={t("search_placeholder")}
                        getSelectionBlockedMessage={getSelectionBlockedMessage}
                      />
                      {validationErrors.includes("participants") && (
                        <Typography
                          color="error"
                          variant="caption"
                          role="alert"
                        >
                          {t("field_required")}
                        </Typography>
                      )}
                    </div>
                  </>
                )}
                {selectedAssignmentType === "multiple_companies" && (
                  <>
                    {usesManagerCompanyScope ? (
                      <DefaultInput
                        label={t("company")}
                        value={managerLockedCompanyDisplayName}
                        readonly={true}
                        mandatory={true}
                        disabled={true}
                      />
                    ) : (
                      <div ref={registerValidationTarget("companies")}>
                        <FormFieldLabel
                          label={t("companies")}
                          htmlFor="lms-assignment-companies"
                          mandatory
                        />
                        <SubjectSearchSelect
                          name="lms-assignment-companies"
                          isMulti={true}
                          value={selectedTargetOptions}
                          loadOptions={loadCompanyOptions}
                          onChange={(value: any) => {
                            clearValidationErrors("companies");
                            handleGenericTargetChange(value);
                          }}
                          isClearable={true}
                          placeholder={t("search_placeholder")}
                          getSelectionBlockedMessage={
                            getSelectionBlockedMessage
                          }
                        />
                        {validationErrors.includes("companies") && (
                          <Typography
                            color="error"
                            variant="caption"
                            role="alert"
                          >
                            {t("field_required")}
                          </Typography>
                        )}
                      </div>
                    )}
                    <FormControlLabel
                      label={t("include_child_companies")}
                      control={
                        <Checkbox
                          checked={includeChildCompanies}
                          onChange={(event) =>
                            setIncludeChildCompanies(event.target.checked)
                          }
                          color="secondary"
                        />
                      }
                    />
                  </>
                )}
                {selectedAssignmentType !== "multiple_departments" &&
                  selectedAssignmentType !== "multiple_participants" &&
                  selectedAssignmentType !== "multiple_companies" && (
                    <div ref={registerValidationTarget("fallbackTarget")}>
                      <FormFieldLabel
                        label={t("subject")}
                        htmlFor="lms-assignment-subject"
                        mandatory
                      />
                      <SubjectSearchSelect
                        name="lms-assignment-subject"
                        isMulti={true}
                        value={selectedTargetOptions}
                        loadOptions={loadFallbackSubjectOptions}
                        onChange={(value: any) => {
                          clearValidationErrors("fallbackTarget");
                          handleGenericTargetChange(value);
                        }}
                        isClearable={true}
                        placeholder={t("search_placeholder")}
                        getSelectionBlockedMessage={getSelectionBlockedMessage}
                      />
                      {validationErrors.includes("fallbackTarget") && (
                        <Typography
                          color="error"
                          variant="caption"
                          role="alert"
                        >
                          {t("field_required")}
                        </Typography>
                      )}
                    </div>
                  )}
                {selectedAssignmentType === "multiple_departments" && (
                  <>
                    {!usesManagerCompanyScope && (
                      <div ref={registerValidationTarget("departmentCompany")}>
                        <FormFieldLabel
                          label={t("company")}
                          htmlFor="lms-assignment-department-company"
                          mandatory
                        />
                        <SubjectSearchSelect
                          name="lms-assignment-department-company"
                          isMulti={false}
                          value={selectedDepartmentCompanies[0]}
                          loadOptions={loadCompanyOptions}
                          onChange={(value: any) => {
                            clearValidationErrors(
                              "departmentCompany",
                              "departments"
                            );
                            if (!value) {
                              setSelectedDepartmentCompanies([]);
                              setSelectedDepartments([]);
                              return;
                            }
                            setSelectedDepartmentCompanies([value]);
                            setSelectedDepartments([]);
                          }}
                          isClearable={true}
                          placeholder={t("search_placeholder")}
                          getSelectionBlockedMessage={
                            getSelectionBlockedMessage
                          }
                        />
                        {validationErrors.includes("departmentCompany") && (
                          <Typography
                            color="error"
                            variant="caption"
                            role="alert"
                          >
                            {t("field_required")}
                          </Typography>
                        )}
                      </div>
                    )}
                    <div ref={registerValidationTarget("departments")}>
                      <FormFieldLabel
                        label={t("departments")}
                        htmlFor="lms-assignment-departments"
                        mandatory
                      />
                      <SubjectSearchSelect
                        name="lms-assignment-departments"
                        isMulti={true}
                        value={selectedDepartments}
                        loadOptions={loadDepartmentOptions}
                        loadAllOptions={loadAllDepartmentOptions}
                        loadAllOptionsResetKey={departmentOptionsScopeKey}
                        onLoadAllOptionsError={handleError}
                        onChange={(value: any) => {
                          clearValidationErrors("departments");
                          setSelectedDepartments(value ?? []);
                        }}
                        isClearable={true}
                        isDisabled={
                          !usesManagerCompanyScope &&
                          selectedDepartmentCompanies.length === 0
                        }
                        placeholder={t("search_placeholder")}
                        getSelectionBlockedMessage={getSelectionBlockedMessage}
                      />
                      {validationErrors.includes("departments") && (
                        <Typography
                          color="error"
                          variant="caption"
                          role="alert"
                        >
                          {t("field_required")}
                        </Typography>
                      )}
                    </div>
                  </>
                )}
                <LmsCustomIntervalFields
                  value={customIntervals}
                  onChange={(value) => {
                    clearValidationErrors(
                      "completionInterval",
                      "validityInterval"
                    );
                    setCustomIntervals(value);
                  }}
                  invalidTargets={validationErrors.filter(
                    (target): target is LmsCustomIntervalValidationTarget =>
                      target === "completionInterval" ||
                      target === "validityInterval"
                  )}
                  registerCompletionTarget={registerValidationTarget(
                    "completionInterval"
                  )}
                  registerValidityTarget={registerValidationTarget(
                    "validityInterval"
                  )}
                />
              </>
            )}
          </DialogContent>
          <DialogActions className={styles.dialogActions}>
            <GenericButton
              type="button"
              variant="outlined"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("cancel")}
            </GenericButton>
            {!isReadOnly && (
              <GenericButton
                type="submit"
                disabled={contextUnavailable || isSubmitting}
              >
                {t("save")}
              </GenericButton>
            )}
          </DialogActions>
        </form>
      </Dialog>
      {errorModal}
    </>
  );
}
